// =====================================================================
// ChairsideModeToggle — Doctor view's 4-mode chart toggle.
// =====================================================================
// Lives at the top of the dynamic charting card itself so the chart
// stays anchored at the same Y across modes; only the paint behavior
// + the context panel below switches.
//
// Each mode gets its own color tone so the active state reads
// unambiguously at a glance — closes the "did I paint into the
// wrong layer?" risk Cameron flagged in the rating.
//
// Extracted from ChartingTab.jsx so Doctor view can render it
// standalone in tx_plan mode (where it bypasses ChartingTab entirely
// to mount the same TreatmentTab the PX tab uses, keeping Chart and
// Tx Plan as a single UI across the app).
// =====================================================================

const CHAIRSIDE_MODES = [
  { value: "chart",        label: "Charting",     tone: "teal",   shortcut: "1" },
  { value: "today",        label: "Today",        tone: "amber",  shortcut: "2" },
  { value: "future_need",  label: "Future Need",  tone: "blue",   shortcut: "3" },
  { value: "tx_plan",      label: "Tx Plan",      tone: "violet", shortcut: "4" },
];

const TUTORIAL_MODE_IDS = {
  chart: "doctor-chart-mode-chart",
  today: "doctor-chart-mode-today",
  future_need: "doctor-chart-mode-future-need",
  tx_plan: "doctor-chart-mode-tx-plan",
};

export default function ChairsideModeToggle({ mode, onChange, disabledModes = [] }) {
  const disabled = new Set(disabledModes);

  return (
    <div
      className="grid w-full grid-cols-2 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 sm:inline-flex sm:w-auto sm:gap-0 sm:p-0.5"
      role="tablist"
      data-testid="doctor-chart-mode-toggle"
      data-tutorial-id="doctor-chart-mode-toggle"
    >
      {CHAIRSIDE_MODES.map((m) => {
        const active = mode === m.value;
        const disabledMode = disabled.has(m.value);
        const activeClasses =
          m.tone === "teal"   ? "bg-teal-600 text-white"
          : m.tone === "violet" ? "bg-violet-600 text-white"
          : m.tone === "amber"  ? "bg-amber-500 text-white"
          : m.tone === "blue"   ? "bg-blue-600 text-white"
          : "bg-slate-900 text-white";
        return (
          <button
            key={m.value}
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={disabledMode ? "true" : undefined}
            onClick={() => {
              if (disabledMode) return;
              onChange(m.value);
            }}
            data-testid={`doctor-chart-mode-${m.value}`}
            data-tutorial-id={TUTORIAL_MODE_IDS[m.value]}
            title={disabledMode ? `${m.label} requires Clinic` : `${m.label} (press ${m.shortcut})`}
            className={`inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 sm:min-h-0 sm:w-auto sm:px-3.5 sm:py-1.5 ${
              active
                ? `${activeClasses} focus:ring-offset-white`
                : disabledMode
                  ? "cursor-not-allowed text-slate-400 opacity-60"
                : "text-slate-600 hover:bg-white"
            }`}
          >
            <span>{m.label}</span>
            <kbd
              aria-hidden
              data-tutorial-id={m.value === "chart" ? "charting-shortcuts" : undefined}
              className={`font-mono text-[9px] leading-none px-1 py-[1px] rounded border ${
                active
                  ? "border-white/30 bg-white/15 text-white/80"
                  : "border-slate-200 bg-white text-slate-400"
              }`}
            >
              {m.shortcut}
            </kbd>
          </button>
        );
      })}
    </div>
  );
}
